import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  EmptyState,
  PageHeader,
  SectionCard,
  StatusBadge,
  ToggleRow,
  TWButton,
  TWConfirmModal,
  TWTabNav,
} from '../../components/ui';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import fleetApi from '../../services/fleetApi';
import AdminOnly from './components/admin-only';
import CommandSheet from './components/command-sheet';
import DeviceAuditTab from './components/device-audit-tab';
import DeviceCommandsTab from './components/device-commands-tab';
import DeviceInstallTab from './components/device-install-tab';
import DeviceOverviewTab from './components/device-overview-tab';
import DeviceRemoteTab from './components/device-remote-tab';
import useFleetPoll, { fleetErrorText } from './use-fleet-poll';
import { isTerminalCommand, relativeSeen, statusLabel, statusTone } from './fleet-utils';

const BACK = '/dashboard/devices';
const SHOT_POLL_MS = 2000;
const SHOT_MAX_POLLS = 30;
const ACTION_FALLBACK = 'Something went wrong. Try again.';
const ROLE_TONES = { kiosk: 'blue', kitchen: 'violet', unassigned: 'amber' };

// One list of tabs. `remote` shows only while Fleet/Config says fleetRemote.
const TABS = [
  { id: 'overview', label: 'Overview', Body: DeviceOverviewTab },
  { id: 'install', label: 'Install', Body: DeviceInstallTab },
  { id: 'commands', label: 'Commands', Body: DeviceCommandsTab },
  { id: 'audit', label: 'Audit', Body: DeviceAuditTab },
  { id: 'remote', label: 'Remote', Body: DeviceRemoteTab, needsRemote: true },
];

const CONFIRMS = {
  reboot: {
    tone: 'danger',
    title: 'Reboot this device?',
    description:
      'The tablet restarts and is offline for a minute or two. An order in progress can be lost.',
    confirmLabel: 'Reboot',
    type: 'reboot',
    toast: 'Reboot requested',
  },
  maintenance: {
    tone: 'warning',
    title: 'Put this device in maintenance mode?',
    description:
      'The kiosk lock, status bar and USB debugging are lifted for 30 minutes so a technician can work on the tablet.',
    confirmLabel: 'Start maintenance',
    type: 'maintenance',
    toast: 'Maintenance mode requested',
  },
  endMaintenance: {
    tone: 'info',
    title: 'End maintenance now?',
    description: 'The tablet goes back to its locked app right away.',
    confirmLabel: 'End maintenance',
    type: 'end_maintenance',
    toast: 'Maintenance ended',
  },
  unenroll: {
    tone: 'danger',
    title: 'Unenroll this device?',
    description:
      'The tablet leaves the fleet and stops taking commands. Type the device name to confirm.',
    confirmLabel: 'Unenroll',
    toast: 'Device unenrolled',
  },
};

const formatClock = (date) =>
  String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0');

const PAGE_CLASS =
  'min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';
const LINK_CLASS =
  'inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

const Shell = ({ children }) => (
  <div className={PAGE_CLASS}>
    <div className="mx-auto max-w-7xl space-y-6">{children}</div>
  </div>
);

const DetailSkeleton = () => (
  <Shell>
    <PageHeader
      backTo={BACK}
      backLabel="Devices"
      title={<SkeletonBlock className="h-8 w-56" />}
    />
    <div className="grid gap-6 lg:grid-cols-2">
      {[0, 1].map((n) => (
        <SectionCard key={n}>
          <div className="space-y-3">
            <SkeletonBlock className="h-4 w-1/3" />
            <SkeletonBlock className="h-4 w-2/3" />
            <SkeletonBlock className="h-4 w-1/2" />
            <SkeletonBlock className="h-4 w-3/4" />
          </div>
        </SectionCard>
      ))}
    </div>
  </Shell>
);

const GUID = /^[0-9a-f-]{36}$/i;

const NotFound = ({ description, retry }) => (
  <Shell>
    <EmptyState
      icon={ExclamationTriangleIcon}
      title="Device not found"
      description={description}
      action={
        <div className="flex items-center justify-center gap-2">
          <Link to={BACK} className={LINK_CLASS}>
            Back to devices
          </Link>
          {retry ? <TWButton onClick={retry}>Retry</TWButton> : null}
        </div>
      }
    />
  </Shell>
);

const NOT_FOUND_TEXT = 'It may have been unenrolled.';

const DeviceDetail = () => {
  const { id } = useParams();
  // Keyed by id: moving to another device starts from clean state. An id that
  // is not a GUID never reaches the API.
  return (
    <AdminOnly>
      {GUID.test(id || '') ? (
        <DeviceDetailView key={id} id={id} />
      ) : (
        <NotFound description={NOT_FOUND_TEXT} />
      )}
    </AdminOnly>
  );
};

const DeviceDetailView = ({ id }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, loading, error, reload } = useFleetPoll(
    () => fleetApi.getDevice(id).then((res) => res.data.data),
    {
      intervalMs: 0,
      deps: [id],
      onRefetchError: (err) => message.error(fleetErrorText(err, 'Could not load the device')),
    },
  );

  // useFleetPoll has no setter: a saved assignment lands here until the next
  // answer from the API replaces it.
  const [patch, setPatch] = useState(null);
  useEffect(() => {
    setPatch(null);
  }, [data]);
  const device = data ? (patch ? { ...data, ...patch } : data) : null;

  // The agent reports telemetry.maintenanceuntil; a confirmed command flips the
  // toggle at once and the next device poll brings the real value back.
  const [maintenanceFlip, setMaintenanceFlip] = useState(null);
  useEffect(() => {
    setMaintenanceFlip(null);
  }, [data]);
  const supportsEnd = typeof device?.agentversioncode === 'number' && device.agentversioncode >= 13;
  // An optimistic open window lapses on its own, so a failed reload cannot
  // leave End maintenance showing after the window.
  const flipUntil = maintenanceFlip?.open ? maintenanceFlip.until.getTime() : null;
  useEffect(() => {
    if (flipUntil === null) return undefined;
    const timer = setTimeout(() => setMaintenanceFlip(null), Math.max(0, flipUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [flipUntil]);
  const activeFlip = maintenanceFlip && !(flipUntil !== null && flipUntil <= Date.now()) ? maintenanceFlip : null;
  const reportedUntil =
    supportsEnd && device.telemetry?.maintenanceuntil ? new Date(device.telemetry.maintenanceuntil) : null;
  const reportedOpen =
    reportedUntil !== null && !Number.isNaN(reportedUntil.getTime()) && reportedUntil.getTime() > Date.now();
  const maintenanceOpen = supportsEnd && (activeFlip ? activeFlip.open : reportedOpen);
  const maintenanceUntil = !maintenanceOpen ? null : activeFlip ? activeFlip.until : reportedUntil;

  // A saved assignment keeps its patch and reloads: the reload supersedes any
  // earlier request still in flight (useFleetPoll drops older answers), so a
  // stale answer cannot overwrite what was just saved.
  const onDeviceChange = (next) => {
    setPatch(next);
    reload();
  };

  // Bumped after every command the header sends, so the Commands tab refetches
  // (and starts polling again) without a tab switch.
  const [commandsVersion, setCommandsVersion] = useState(0);
  const commandSent = () => setCommandsVersion((n) => n + 1);

  const [fleetRemote, setFleetRemote] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fleetApi
      .getConfig()
      .then((res) => {
        if (!cancelled) setFleetRemote(res?.data?.data?.fleetRemote === true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  // Unenroll's ADB door: set from the model each time the dialog opens.
  const [leaveAdbOpen, setLeaveAdbOpen] = useState(false);
  const [sheet, setSheet] = useState({ open: false, initialType: undefined });
  const [shooting, setShooting] = useState(false);

  const mounted = useRef(true);
  // Synchronous in-flight guards: two clicks in one event tick both see the
  // same (stale) state, a ref does not.
  const confirmBusy = useRef(false);
  const shootingRef = useRef(false);
  const shotTimer = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(shotTimer.current);
    };
  }, []);

  const targets = useMemo(
    () => ({ deviceIds: [id], label: device?.name || device?.serialnumber || '' }),
    [id, device?.name, device?.serialnumber],
  );

  if (error) {
    const notFound = error.response?.status === 404;
    return (
      <NotFound
        description={notFound ? NOT_FOUND_TEXT : fleetErrorText(error, NOT_FOUND_TEXT)}
        retry={notFound ? undefined : reload}
      />
    );
  }
  if (loading || !device) return <DetailSkeleton />;

  const requestedTab = searchParams.get('tab');
  const tabs = TABS.filter((t) => !t.needsRemote || fleetRemote);
  const active = tabs.find((t) => t.id === requestedTab) || tabs[0];
  const ActiveBody = active.Body;

  const onTabChange = (next) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === tabs[0].id) params.delete('tab');
        else params.set('tab', next);
        params.delete('page');
        return params;
      },
      { replace: true },
    );

  const runConfirmed = async () => {
    const config = CONFIRMS[confirm];
    if (!config || confirmBusy.current) return;
    confirmBusy.current = true;
    setBusy(true);
    try {
      if (confirm === 'unenroll') {
        await fleetApi.unenrollDevice(id, { leaveadbopen: leaveAdbOpen });
        message.success(config.toast);
        navigate(BACK);
        return;
      }
      await fleetApi.sendCommand(id, { type: config.type, args: {} });
      message.success(config.toast);
      if (!(typeof device?.agentversioncode === 'number' && device.agentversioncode >= 13)) {
        // an agent below 13 cannot end a window: no optimistic toggle
      } else if (confirm === 'maintenance') {
        setMaintenanceFlip({ open: true, until: new Date(Date.now() + 30 * 60 * 1000) });
      } else if (confirm === 'endMaintenance') {
        setMaintenanceFlip({ open: false, until: null });
      }
      setConfirm(null);
      commandSent();
      reload();
    } catch (err) {
      message.error(fleetErrorText(err, ACTION_FALLBACK));
    } finally {
      confirmBusy.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  // Poll the screenshot command every 2 s, at most 30 times. A missed poll
  // (network) counts as a poll and the chain carries on.
  const pollScreenshot = (commandId, polled) => {
    shotTimer.current = setTimeout(async () => {
      let cmd = null;
      try {
        cmd = (await fleetApi.getCommand(commandId)).data.data;
      } catch (err) {
        cmd = null;
      }
      if (!mounted.current) return;
      if (cmd && isTerminalCommand(cmd.status)) {
        shootingRef.current = false;
        setShooting(false);
        commandSent();
        if (cmd.status === 'done') {
          reload();
          message.success('Screenshot updated');
        } else {
          message.error(cmd.error || 'Screenshot failed');
        }
        return;
      }
      if (polled + 1 >= SHOT_MAX_POLLS) {
        shootingRef.current = false;
        setShooting(false);
        message.warning('Screenshot is taking long; check the Commands tab');
        return;
      }
      pollScreenshot(commandId, polled + 1);
    }, SHOT_POLL_MS);
  };

  const takeScreenshot = async () => {
    if (shootingRef.current) return;
    shootingRef.current = true;
    setShooting(true);
    try {
      const res = await fleetApi.sendCommand(id, { type: 'screenshot', args: {} });
      if (!mounted.current) return;
      commandSent();
      pollScreenshot(res.data.data.id, 0);
    } catch (err) {
      shootingRef.current = false;
      if (!mounted.current) return;
      setShooting(false);
      message.error(fleetErrorText(err, ACTION_FALLBACK));
    }
  };

  const description = (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <StatusBadge
        dot
        size="sm"
        tone={statusTone(device.status)}
        label={statusLabel(device.status)}
      />
      <span>{device.lastseenat ? 'Seen ' + relativeSeen(device.lastseenat) : '—'}</span>
      <StatusBadge
        size="sm"
        tone={ROLE_TONES[device.role] || 'slate'}
        label={statusLabel(device.role)}
      />
      <span>{[device.serialnumber, device.model].filter(Boolean).join(' · ')}</span>
    </span>
  );

  const openSheet = (initialType) => setSheet({ open: true, initialType });

  // rk tablets (Rockchip) have no helper app to re-open ADB later, so the
  // door stays open by default there (R-12); the admin can flip it.
  const openUnenroll = () => {
    setLeaveAdbOpen(/^rk/i.test(device.model || ''));
    setConfirm('unenroll');
  };

  return (
    <Shell>
      <PageHeader
        title={device.name || device.serialnumber || device.id}
        backTo={BACK}
        backLabel="Devices"
        description={description}
        actions={
          <>
            <TWButton variant="default" onClick={takeScreenshot} loading={shooting}>
              Screenshot
            </TWButton>
            <TWButton variant="default" onClick={() => setConfirm('reboot')}>
              Reboot
            </TWButton>
            <TWButton variant="default" onClick={() => openSheet('restart_app')}>
              Restart app
            </TWButton>
            {maintenanceOpen ? (
              <>
                <TWButton variant="default" onClick={() => setConfirm('endMaintenance')}>
                  End maintenance
                </TWButton>
                {maintenanceUntil && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    until {formatClock(maintenanceUntil)}
                  </span>
                )}
              </>
            ) : (
              <TWButton variant="default" onClick={() => setConfirm('maintenance')}>
                Maintenance mode
              </TWButton>
            )}
            <TWButton variant="ghost" onClick={() => openSheet(undefined)}>
              More…
            </TWButton>
            <TWButton variant="danger" onClick={openUnenroll}>
              Unenroll
            </TWButton>
          </>
        }
      />

      <TWTabNav
        tabs={tabs.map(({ id: tabId, label }) => ({ id: tabId, label }))}
        value={active.id}
        onChange={onTabChange}
        ariaLabel="Device sections"
      />

      <div className="space-y-6">
        <ActiveBody
          key={device.id}
          device={device}
          onDeviceChange={onDeviceChange}
          commandsVersion={commandsVersion}
        />
      </div>

      {Object.entries(CONFIRMS).map(([key, config]) => (
        <TWConfirmModal
          key={key}
          open={confirm === key}
          onClose={() => {
            if (!busy) setConfirm(null);
          }}
          onConfirm={runConfirmed}
          tone={config.tone}
          title={config.title}
          description={config.description}
          confirmLabel={config.confirmLabel}
          loading={busy && confirm === key}
          requireTypedConfirmation={
            key === 'unenroll' ? device.name || device.serialnumber || device.id : undefined
          }
        >
          {key === 'unenroll' && confirm === 'unenroll' ? (
            <ToggleRow
              title="Leave network ADB open for re-enrolment"
              description={
                typeof device.agentversioncode === 'number' && device.agentversioncode < 7
                  ? 'This agent version closes ADB on unenroll; update the agent first.'
                  : 'Anyone on the store Wi-Fi can reach the tablet until it is re-enrolled.'
              }
              enabled={leaveAdbOpen}
              onChange={setLeaveAdbOpen}
              disabled={busy}
            />
          ) : null}
        </TWConfirmModal>
      ))}

      <CommandSheet
        open={sheet.open}
        onClose={() => setSheet((prev) => ({ ...prev, open: false }))}
        targets={targets}
        onSent={() => {
          commandSent();
          reload();
        }}
        initialType={sheet.initialType}
        endMaintenanceSupported={supportsEnd}
      />
    </Shell>
  );
};

export default DeviceDetail;
